import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './AppStore.css';
import { darken, useCtaPress } from './shared';
import type { TemplateProps } from './types';

const DEFAULT_CTA_COLOR = '#000000';
// 顶部 header 全程骨架占位的统计列，仅用作 React key（内容完全相同）。
const STAT_SLOTS = ['downloads', 'rating', 'age', 'category'] as const;

/**
 * App Store 风格的「加载骨架 + 下载引导弹窗」落地页。
 * 顶部与弹窗内容全程 shimmer 占位（伪装"还在加载"、不暴露真实 app 身份），
 * 800ms 后底部滑出下载弹窗。弹窗按钮接管 cloak 的 onAccept，并在
 * awaiting-click 前显示 checking 态。移植自 app-store-skeleton.html。
 */
export function AppStoreTemplate({ state, onAccept, buttonColor }: TemplateProps) {
  const { t } = useTranslation();
  const ready = state.status === 'awaiting-click';
  const [sheetOpen, setSheetOpen] = useState(false);
  const { pressed, onClick } = useCtaPress(onAccept);
  const color = buttonColor?.trim() || DEFAULT_CTA_COLOR;

  // 骨架先展示一会儿，再滑出下载引导弹窗（与原始 800ms 一致）。
  useEffect(() => {
    const id = setTimeout(() => setSheetOpen(true), 800);
    return () => clearTimeout(id);
  }, []);

  return (
    <div className="as-skeleton min-h-dvh w-full bg-white">
      <div className="as-page">
        {/* top nav */}
        <div className="nav">
          <div className="back">
            <span className="chev" />
            <span className="sk back-tx" />
          </div>
          <div className="sk share" />
        </div>

        {/* app header（全程骨架占位，不暴露真实 app 身份） */}
        <div className="app-head">
          <div className="sk icon" />
          <div className="meta">
            <div className="sk name" />
            <div className="sk name l2" />
            <div className="sk dev" />
            <div className="head-actions">
              <div className="sk get-btn" />
              <div className="sm-icons">
                <div className="sk si" />
                <div className="sk si" />
              </div>
            </div>
          </div>
        </div>

        {/* stats bar */}
        <div className="stats">
          {STAT_SLOTS.map((slot) => (
            <div className="stat" key={slot}>
              <div className="sk s-top" />
              <div className="sk s-mid" />
              <div className="sk s-bot" />
            </div>
          ))}
        </div>

        {/* screenshots */}
        <div className="shots">
          <div className="sk shot" />
          <div className="sk shot" />
          <div className="sk shot" />
        </div>

        {/* description */}
        <section>
          <div className="sk line w-full" />
          <div className="sk line w-95" />
          <div className="sk line w-90" />
          <div className="sk line w-full" />
          <div className="sk line w-55" />
        </section>

        {/* what's new */}
        <section>
          <div className="sec-head">
            <div className="sk sec-title" />
            <div className="sk sec-more" />
          </div>
          <div className="sk line w-40" style={{ marginBottom: 16 }} />
          <div className="sk line w-full" />
          <div className="sk line w-80" />
        </section>

        {/* ratings & reviews */}
        <section>
          <div className="sec-head">
            <div className="sk sec-title" />
            <div className="sk sec-more" />
          </div>
          <div className="rate-summary">
            <div className="sk big" />
            <div className="bars">
              <div className="sk b w-full" />
              <div className="sk b w-90" />
              <div className="sk b w-70" />
              <div className="sk b w-55" />
              <div className="sk b w-40" />
            </div>
          </div>
          <div className="review">
            <div className="r-head">
              <div className="sk r-title" />
              <div className="sk r-stars" />
            </div>
            <div className="sk line w-full" />
            <div className="sk line w-95" />
            <div className="sk line w-70" />
          </div>
        </section>
      </div>

      {/* loading indicator */}
      <div className="loading">
        <span className="spinner" />
        <span className="tx">{t('appstore.loading')}</span>
      </div>

      {/* download bottom sheet（常驻，不可点遮罩关闭——保持转化入口始终在前） */}
      <div className={`overlay${sheetOpen ? ' show' : ''}`}>
        <div className="sheet">
          <div className="grabber" />
          {/* logo/名称同样骨架占位 —— 弹窗不暴露真实 app 身份 */}
          <div className="s-app">
            <div className="sk s-ic" />
            <div className="s-txt">
              <div className="sk s-name" />
              <div className="sk s-sub" />
            </div>
          </div>
          <div className="s-tip">{t('appstore.tip')}</div>
          <button
            type="button"
            className="dl-btn"
            disabled={!ready}
            onClick={onClick}
            style={
              ready
                ? { backgroundColor: pressed ? darken(color, 24) : color }
                : { backgroundColor: '#c7c7cc', cursor: 'default' }
            }
          >
            {ready ? (
              t('appstore.cta')
            ) : (
              <span className="dl-loading">
                <Loader2 className="h-[18px] w-[18px] animate-spin" />
                {t('appstore.checking')}
              </span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
